import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://newerp.awasthivitality.com/api";
const getSessionToken = () =>
  (localStorage.getItem("session_token") || "").trim();

const appendToken = (url) => {
  const token = getSessionToken();
  if (!token) return url;

  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has("token")) {
      parsed.searchParams.append("token", token);
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

const GenerateInvoice = () => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [descriptionOptions, setDescriptionOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [discount, setDiscount] = useState();
  const [items, setItems] = useState(
    Array.from({ length: 5 }, () => ({ description: "", quantity: 1, amount: 0 }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [leftMargin, setLeftMargin] = useState(window.innerWidth >= 1024 ? 250 : 0);

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setLeftMargin(window.innerWidth >= 1024 ? 250 : 0);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchAllPatients();
    fetchProductsForInvoice();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      searchPatients(searchTerm);
    }, 250);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm]);

  const fetchAllPatients = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        appendToken(`${API_BASE}/patients/search`),
        { headers: { Accept: "application/json" } }
      );
      const data = await response.json();
      const arr = Array.isArray(data?.data) ? data.data : [];
      setPatients(arr);
      setFilteredPatients(arr);
    } catch {
      setPatients([]);
      setFilteredPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const searchPatients = async (query) => {
    try {
      setLoading(true);
      const url = query?.trim()
        ? `${API_BASE}/patients/search?q=${encodeURIComponent(query)}`
        : `${API_BASE}/patients/search`;

      const response = await fetch(appendToken(url), {
        headers: { Accept: "application/json" },
      });
      const data = await response.json();
      setFilteredPatients(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setFilteredPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsForInvoice = async () => {
    try {
      const res = await fetch(`${API_BASE}/products/for-invoice`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      const formatted = list.map((item) => ({
        id: item.product_id,
        label: item.product_name,
        amount: Number(item.final_price ?? 0),
      }));
      setDescriptionOptions(formatted);
    } catch {
      setDescriptionOptions([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm(patient.display_text || patient.name || "");
    setShowDropdown(false);
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] =
      field === "quantity" || field === "amount"
        ? Number(value || 0)
        : value;
    setItems(updated);
  };

  const handleSelectChange = (index, valueId) => {
    const option = descriptionOptions.find(
      (o) => String(o.id) === String(valueId)
    );
    const updated = [...items];

    if (option) {
      updated[index].description = option.label;
      updated[index].amount = option.amount;
    } else {
      updated[index].description = "";
      updated[index].amount = 0;
    }
    setItems(updated);
  };

  const uiTotal = items.reduce(
    (sum, item) => sum + Number(item.amount) * Number(item.quantity),
    0
  );

  const finalTotal = Math.max(uiTotal - Number(discount || 0), 0);

  const handleSubmit = async () => {
    if (!selectedPatient) return alert("Please select a patient!");

    const validItems = items.filter(
      (i) => i.description.trim() !== "" && Number(i.amount) > 0
    );

    if (!validItems.length) return alert("Add at least one item!");

    // ✅ FIXED PAYLOAD (MATCHES BACKEND)
    const payload = {
      patient_id: selectedPatient.patient_id,
      invoice_date: date,
      discount_amount: Number(discount || 0),
      products: validItems.map((i) => ({
        description: i.description,
        quantity: Number(i.quantity),
        unit_price: Number(i.amount),
      })),
    };

    const token = getSessionToken();
    const url = `${API_BASE}/invoices/generate?token=${token}`;

    try {
      setSubmitting(true);
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result?.success) {
        alert(
          `Invoice Created!\nInvoice No: ${result.data.invoice_no}\nTotal Amount: ₹${finalTotal.toFixed(
            2
          )}`
        );
        navigate("/InvoiceList");
      } else {
        alert(result?.message || "Invoice creation failed!");
      }
    } catch {
      alert("Server error creating invoice.");
    } finally {
      setSubmitting(false);
    }
  };
 const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "20px",
      background: "#f8f9fa",
      minHeight: "100vh",
      marginLeft: `${leftMargin}px`,
      transition: "0.3s",
    },
    header: { fontSize: "28px", textAlign: "center", marginBottom: "20px" },
    label: { fontWeight: "bold", marginBottom: "8px", display: "block" },
    input: {
      width: "100%",
      padding: "10px",
      border: "2px solid #ddd",
      borderRadius: "6px",
      fontSize: "15px",
    },
    table: {
      width: "100%",
      marginTop: "20px",
      background: "white",
      borderRadius: "10px",
      overflow: "hidden",
      borderCollapse: "collapse",
    },
    th: { background: "#343a40", color: "white", padding: "12px", textAlign: "left" },
    td: { padding: "12px", borderBottom: "1px solid #ddd" },
    numberInput: { width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #bbb", textAlign: "right" },
    submitButton: { width: "100%", padding: "14px", background: "#28a745", color: "white", fontSize: "18px", marginBottom: "50px", borderRadius: "8px", cursor: "pointer", marginTop: "20px" },
    footerBox: { marginTop: "10px", maxWidth: "300px", marginLeft: "auto" },
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Generate Invoice</h2>

      {/* PATIENT + DATE */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "300px" }}>
          <label style={styles.label}>Patient</label>
          <input
            type="text"
            placeholder="Search patient..."
            value={searchTerm}
            onChange={handleSearchChange}
            ref={searchRef}
            style={styles.input}
            onFocus={() => setShowDropdown(true)}
          />
          {showDropdown && (
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                background: "white",
                border: "1px solid #ddd",
                width: "300px",
                maxHeight: "300px",
                overflowY: "auto",
                zIndex: 999,
              }}
            >
              {filteredPatients.map((p) => (
                <div
                  key={p.patient_id}
                  onClick={() => handlePatientSelect(p)}
                  style={{ padding: "10px", borderBottom: "1px solid #eee", cursor: "pointer" }}
                >
                  {p.name} - {p.mobile}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: "200px" }}>
          <label style={styles.label}>Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={styles.input} />
        </div>
      </div>

      {/* ITEMS TABLE */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>S.No</th>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>Qty</th>
            <th style={styles.th}>Amount</th>
          </tr>
        </thead>

        <tbody>
          {items.map((item, index) => {
            const selectedOption = descriptionOptions.find((o) => o.label === item.description);
            const selectedId = selectedOption ? selectedOption.id : "";

            return (
              <tr key={index}>
                <td style={styles.td}>{index + 1}</td>
                <td style={styles.td}>
                  <select
                    value={selectedId}
                    onChange={(e) => handleSelectChange(index, e.target.value)}
                    style={styles.input}
                  >
                    <option value="">Select Description</option>
                    {descriptionOptions.map((op) => (
                      <option key={op.id} value={op.id}>
                        {op.label} - ₹{op.amount}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={styles.td}>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                    style={styles.numberInput}
                  />
                </td>
                <td style={styles.td}>
                  <input
                    type="number"
                    min="0"
                    value={item.amount}
                    onChange={(e) => handleItemChange(index, "amount", e.target.value)}
                    style={styles.numberInput}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* FOOTER: DISCOUNT + TOTAL */}
      {/* FOOTER: DISCOUNT + TOTAL */}
      <div style={{ display: "flex", gap: "20px", maxWidth: "600px", marginLeft: "auto", marginTop: "20px" }}>
        <div style={{ flex: 1 }}>
          <label style={styles.label}>Discount (₹)</label>
          <input
            type="number"
            min="0"
            value={discount}
            onChange={(e) => setDiscount(Number(e.target.value || 0))}
            style={styles.input}
          />
        </div>

        <div style={{ flex: 1 }}>
          <label style={styles.label}>Total Amount (After Discount)</label>
          <input
            type="number"
            value={finalTotal.toFixed(2)}
            readOnly
            style={{ ...styles.numberInput, fontWeight: "bold", background: "#f8f9fa" }}
          />
        </div>
      </div>


      <button
        style={styles.submitButton}
        onClick={handleSubmit}
        disabled={submitting || !selectedPatient}
      >
        {submitting ? "Generating..." : "Generate Invoice"}
      </button>
    </div>
  );
  // UI remains EXACTLY the same (unchanged)
  // ... your JSX return stays unchanged ...
};

export default GenerateInvoice;